import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../components/Breadcrumbs";
import { PageShell } from "../../features/shared/layouts/PageShell";
import { PageHeader } from "../../features/shared/ui/layouts/PageHeader";
import { useBreadcrumbBack } from "../../components/useBreadcrumbBack";

const pageStackClasses = "flex flex-col gap-8 text-slate-900";
const ghostButtonClasses = "pm-btn pm-btn-ghost";

export function PrivacyPolicyPage() {
  const { t } = useTranslation();
  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("navigation.home"), to: "/" },
      { label: t("legal.privacy.title") },
    ];
  }, [t]);
  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    includeHome: false,
    fallback: { to: "/" },
  });
  type PrivacySection = {
    heading: string;
    body?: string[];
    list?: string[];
  };
  const sections = t("legal.privacy.sections", {
    returnObjects: true,
  }) as PrivacySection[];
  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} includeHome={false} />
      <PageHeader
        title={t("legal.privacy.title")}
        subtitle={
          <p className="text-sm text-slate-500">
            {t("legal.common.last_updated", {
              date: new Date().toLocaleDateString(),
            })}
          </p>
        }
        actions={
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleBack}
          >
            <span aria-hidden>←</span> {t("actions.back")}
          </button>
        }
      />

      {sections.map((section) => (
        <section key={section.heading}>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-slate-900">{section.heading}</h2>
          {section.body?.map((paragraph) => (
            <p key={paragraph} className="text-slate-600 mb-4 leading-relaxed">{paragraph}</p>
          ))}
          {section.list && (
            <ul className="list-disc pl-5 mb-4 text-slate-600 space-y-1">
              {section.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </PageShell>
  );
}
